import React, { InputHTMLAttributes, useId } from "react";
import { Button, ButtonProps } from "./Button";
import { HelpTooltip, HelpTooltipProps } from "./HelpTooltip";

export interface InputProps
  extends InputHTMLAttributes<HTMLInputElement & HTMLTextAreaElement> {
  variant?: "outlined" | null;
  asTextarea?: boolean;
  rows?: number;
  inputClass?: string;
  example?: string;
  help?: Pick<HelpTooltipProps, "label" | "content">;
}

export const Input: React.FC<InputProps> = ({
  placeholder,
  id: _id,
  className,
  inputClass,
  example,
  help,
  asTextarea = false,
  rows = 4,
  ...props
}) => {
  const id = _id || useId();

  const baseClasses = [
    "block",
    "px-2.5",
    "pb-2.5",
    "pt-4",
    "w-full",
    "text-sm",
    "text-gray-900",
    "bg-transparent",
    "rounded-lg",
    "border",
    "border-gray-300",
    "appearance-none",
    "focus:outline-none",
    "focus:ring-2",
    "focus:ring-blue-600",
    "focus:border-blue-600",
    "peer",
    help ? "pr-9" : "",
  ].join(" ");

  return (
    <div className={["relative", className].join(" ")}>
      {asTextarea ? (
        <textarea
          {...props}
          id={id}
          rows={rows}
          className={[baseClasses, "resize-none", inputClass].join(" ")}
          placeholder={example || " "}
        />
      ) : (
        <input
          id={id}
          type="text"
          placeholder={example || " "}
          className={[baseClasses, inputClass].join(" ")}
          {...props}
        />
      )}

      <label
        htmlFor={id}
        className={[
          "absolute",
          "cursor-text",
          "text-sm",
          "text-gray-500",
          "duration-300",
          "transform",
          "-translate-y-4",
          "scale-75",
          "top-2",
          "left-2.5",
          "z-10",
          "origin-[0]",
          "bg-white",
          "px-1",
          ...(!example
            ? [
                "peer-placeholder-shown:translate-y-2",
                "peer-placeholder-shown:scale-100",
              ]
            : []),
          "peer-focus:-translate-y-4",
          "peer-focus:scale-75",
          "peer-focus:text-blue-600",
        ].join(" ")}
      >
        {placeholder}
      </label>
      {help && (
        <span className="absolute right-1.5 top-1.5 z-20 hover:z-[70] focus-within:z-[70]">
          <HelpTooltip {...help} align="right" />
        </span>
      )}
    </div>
  );
};

export const InputWithButton: React.FC<{
  inputProps: InputProps;
  buttonProps: ButtonProps;
}> = (props) => {
  return (
    <div className="flex">
      <Input
        className="grow"
        inputClass="border-r-0 rounded-r-none"
        {...props.inputProps}
      />
      <Button
        {...props.buttonProps}
        className={`p-6 border-s-none rounded-s-none ${props.buttonProps.className}`}
      />
    </div>
  );
};

export const InputWithIcon: React.FC<{
  inputProps: InputProps;
  icon: React.ReactNode;
  iconColor?: string;
}> = ({ inputProps, icon, iconColor = "text-gray-500" }) => {
  return (
    <div className="relative">
      <div className="absolute top-[55%] -translate-y-1/2 left-0 pl-3 pointer-events-none">
        <div className={iconColor}>{icon}</div>
      </div>
      <Input
        {...inputProps}
        inputClass={`${inputProps.inputClass || ""} pl-10`}
      />
    </div>
  );
};
